import React, {
  useCallback, useEffect, useRef, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { debounce } from 'lodash'
import cn from 'classnames'
import { CopyButton } from 'common/components/CopyButton'
import {
  LOCALISE_SEPARATORS, ROUTE_PARAMS, ROUTES, SEARCH_DEBOUNCE_DELAY
} from 'common/constants'
import { useMobileMediaQuery } from 'common/hooks/mediaQuery'
import { EmptyMyNetwork } from 'common/icons/EmptyMyNetwork'
import { MobilyEmptyMyNetwork } from 'common/icons/MobilyEmptyMyNetwork'
import { SpinnerIcon } from 'common/icons/SpinnerIcon'
import { AddButton } from 'features/AddButton'
import { CreateOrEditGroupModal } from 'features/Contacts/Network/MyNetwork/components/CreateOrEditGroupModal'
import { EmptyInviteContainer } from 'features/Contacts/Network/MyNetwork/components/EmptyInviteContainer'
import { actions as actionsHome } from 'features/Home/actions'
import { getGlobalSearchUsers } from 'features/MyProfile/actions'
import { SearchInput } from 'features/SearchInput'
import { SearchStateType } from 'features/SearchInput/types'
import { ShareModalWithDiscard } from 'features/ShareModal'
import { selectInviteViaLinkTranslations, selectMyNetworkTranslations, selectToastMessages } from 'features/Translations/selectors'
import styles from './styles.module.sass'

const MIN_SEARCH_LENGTH = 3

interface IWelcomeContacts {
  searchString: string,
  setSearchString: React.Dispatch<React.SetStateAction<string>>,
  autoFocus?: boolean
  inviteLink: string
}

export const WelcomeContacts: React.FC<IWelcomeContacts> = ({
  searchString, setSearchString, autoFocus, inviteLink
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const myNetworkTranslations = useSelector(selectMyNetworkTranslations)
  const inviteViaLinkTranslations = useSelector(selectInviteViaLinkTranslations)
  const toastMessages = useSelector(selectToastMessages)
  const isMobile = useMobileMediaQuery()
  const [isOpenShareModal, setIsOpenShareModal] = useState(false)
  const [isOpenCreateGroupModal, setIsOpenCreateGroupModal] = useState(false)
  const [loadingGlobalSearchUsers, setLoadingGlobalSearchUsers] = useState(false)
  const activeRequestsRef = useRef(0)

  const isSearchStringValid = (value: string): boolean => {
    return value?.trim()?.length >= MIN_SEARCH_LENGTH
  }
  const debouncedSearch = useCallback(debounce(async (query: string) => {
    setLoadingGlobalSearchUsers(true)
    activeRequestsRef.current += 1
    const users = await getGlobalSearchUsers(query, dispatch, toastMessages)
    if (isSearchStringValid(query)) {
      dispatch(actionsHome.setGlobalSearchUsers(users))
    }

    activeRequestsRef.current -= 1
    if (activeRequestsRef.current === 0) {
      setLoadingGlobalSearchUsers(false)
    }
  }, SEARCH_DEBOUNCE_DELAY), [dispatch])

  const handleSearchChange = (value: string) => {
    setSearchString(value)
    if (isSearchStringValid(value)) {
      debouncedSearch(value)
    } else {
      dispatch(actionsHome.setGlobalSearchUsers([]))
    }
  }

  const handleCreateGroupButtonClick = () => {
    setIsOpenCreateGroupModal(true)
  }

  const handleInviteButtonClick = () => {
    setIsOpenShareModal(true)
  }

  const handleSelectGroup = (groupRef: string) => {
    setIsOpenCreateGroupModal(false)
    if (groupRef) {
      history.push({ pathname: ROUTES.GROUP.replace(ROUTE_PARAMS.GROUPREF, groupRef) })
    }
  }

  const dropDownList = [
    {
      title: myNetworkTranslations.emptyNetworkShareModalTitle,
      onClick: handleInviteButtonClick
    },
    {
      title: myNetworkTranslations.groupDropdownButtonNewGroup,
      onClick: handleCreateGroupButtonClick
    }
  ]

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  return (
    <div className={styles.welcomeContactsContainer}>
      <div className={cn(styles.emptySearchContainer)}>
        <SearchInput
          onChange={handleSearchChange}
          value={searchString}
          placeholder={myNetworkTranslations.search}
          state={SearchStateType.WHITE}
          autoFocus={autoFocus}
        />
        <AddButton buttonTitle={myNetworkTranslations.emptyNetworkDropDownTitle} dropDownList={dropDownList} />
      </div>
      {loadingGlobalSearchUsers
        ? <div className={styles.emptyNetworkSpinner}><SpinnerIcon spinner /></div>
        : (
          <EmptyInviteContainer
            title={myNetworkTranslations.contactsEmptyTitle}
            description={myNetworkTranslations.contactsEmptyDescription}
            icon={isMobile ? <MobilyEmptyMyNetwork /> : <EmptyMyNetwork />}
            imgAlt={myNetworkTranslations.contactsEmptyTitle}
            copyButton={(
              <CopyButton
                shareLink={inviteLink}
                title={inviteViaLinkTranslations.copyLinkShort}
                copiedTitle={inviteViaLinkTranslations.copyToClipBoardShort}
                showLink
              />
          )}
          />
        )}
      <CreateOrEditGroupModal
        isOpen={isOpenCreateGroupModal}
        onClose={() => setIsOpenCreateGroupModal(false)}
        selectGroup={handleSelectGroup}
      />
      {inviteLink && (
        <ShareModalWithDiscard
          inviteLink={inviteLink}
          isOpen={isOpenShareModal}
          onClose={() => setIsOpenShareModal(false)}
          modalTitle={myNetworkTranslations.networkShareModalHeader}
          shareOptions={{
            subject: inviteViaLinkTranslations.welcomeContactsShareLinkSubject,
            bodyText: inviteViaLinkTranslations.welcomeContactsShareLinkBody
              ?.replace(LOCALISE_SEPARATORS.NETWORK.LINK, inviteLink)
          }}
        />
      )}
    </div>
  )
}
